import * as React from 'react';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import isNil from 'lodash/isNil';
import classnames from 'classnames';

import CharacterCount from 'components/character-count/CharacterCount';

import { getLinesNumberForTextAreaElement } from './utils';
import { expandDataProps } from 'utils/helper/common';

import type { Data, TextEntry, TextEntryKeyboardEvent } from 'types';

import './input.scss';

const MULTILINE_OFFSET = 0;
const MULTILINE_MIN_HEIGHT = 90;
const MULTILINE_MAX_HEIGHT = 400;
const MULTILINE_LINE_HEIGHT = 22;

export type Width = 'responsive' | 'fixed' | 'wide' | 'short' | 'extra-wide';

type Props = {
  id?: string,
  type?: string,
  name?: string,
  value: string,
  size?: number,
  max?: number,
  min?: number,
  step?: number,
  disabled?: boolean,
  isEditable?: boolean,
  isNotEditable?: boolean,
  required?: boolean,
  multiline?: boolean,
  placeholder?: string,
  autoComplete?: string,
  autoFocus?: boolean,
  refCallback?: (a: any) => void,
  hint?: string,
  hintAlign?: 'left' | 'right',
  width?: Width,
  align?: 'left' | 'right',
  maxLength?: number,
  className?: string,
  readOnly?: boolean,
  hintClassName?: string,
  focusOnMount?: boolean,
  onChange?: (a: any) => void,
  onKeyPress?: (b: TextEntryKeyboardEvent, a: Data) => void,
  onFocus?: (a: any) => void,
  onBlur?: (b: React.SyntheticEvent<TextEntry>, a: Data) => void,
  onClick?: (a: any) => void,
  onKeyDown?: (a: React.KeyboardEvent<TextEntry>) => void,
  onResize?: (a: number) => void,
  data?: Data,
  charCount: Partial<{
    curCount: number,
    maxChars: number,
    showCountOnly?: boolean
  }>,
  inputProps?: {
    [x: string]: any
  },
  minMultilineHeight?: number
};

class Input extends React.PureComponent<Props> {
  static defaultProps = {
    align: 'left',
    type: 'text',
    placeholder: '',
    width: 'fixed',
    hintAlign: 'left',
    className: '',
    hintClassName: '',
    focusOnMount: false,
    isEditable: true,
    isNotEditable: false,
    charCount: {}
  };

  input: TextEntry | undefined | null = null;

  componentDidMount() {
    const { multiline, focusOnMount } = this.props;

    if (multiline) {
      this.resize();
    }

    if (this.input && focusOnMount) {
      this.input.focus();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { multiline, value } = this.props;

    // resize textarea right after component mounts
    if (multiline && value !== prevProps.value) {
      this.resize();
    }
  }

  private getNewElementHeight(scrollHeight: number, lines: number): number {
    const { minMultilineHeight } = this.props;
    const minHeight = minMultilineHeight || MULTILINE_MIN_HEIGHT;

    if (!isNil(minMultilineHeight)) {
      if (lines === 1) {
        return minMultilineHeight;
      }
    }

    return Math.min(
      Math.max(scrollHeight + MULTILINE_OFFSET, minHeight),
      MULTILINE_MAX_HEIGHT
    );
  }

  resize(): void {
    const { onResize } = this.props;
    const element = this.input;

    if (!element) {
      return;
    }

    element.style.height = 'auto';

    const linesPerElement = getLinesNumberForTextAreaElement(
      element as HTMLTextAreaElement,
      MULTILINE_LINE_HEIGHT
    );

    const newHeight = this.getNewElementHeight(element.scrollHeight, linesPerElement);
    element.style.height = `${newHeight}px`;

    if (onResize) {
      onResize(newHeight);
    }
  }

  handleBlur = (event: React.SyntheticEvent<TextEntry>): void => {
    const { onBlur } = this.props;
    const data = this.input ? this.input.dataset : {};

    if (onBlur) {
      onBlur(event, data);
    }
  };

  handleKeyPress = (event: TextEntryKeyboardEvent): void => {
    const { onKeyPress } = this.props;
    const data = this.input ? this.input.dataset : {};

    if (onKeyPress) {
      onKeyPress(event, data);
    }
  };

  handleClick = (event: React.SyntheticEvent<TextEntry>): void => {
    const { onClick } = this.props;

    if (onClick) {
      onClick(event);
    }
  };

  handleTextareaChanged = (event: any): void => {
    const { multiline, onChange } = this.props;

    if (multiline) {
      this.resize();
    }

    if (onChange) {
      onChange(event);
    }
  };

  handleRefCallback = (ref: any): void => {
    const { refCallback } = this.props;

    if (refCallback) {
      refCallback(ref);
    }

    this.input = ref;
  };

  renderCharacterCount(): React.ReactNode | undefined | null {
    const { charCount } = this.props;

    if (!isObject(charCount) || isEmpty(charCount)) {
      return null;
    }

    return (
      <div className="input__char-count">
        <CharacterCount
          charCount={charCount.curCount}
          charLimit={charCount.maxChars}
          showCountOnly={charCount.showCountOnly}
          align="right"
          size="normal"
          highlightOnError
        />
      </div>
    );
  }

  render() {
    // `refCallback`, `focusOnMount` should be excluded to avoid React warnings
    const {
      align,
      width,
      hint,
      hintAlign,
      focusOnMount,
      multiline,
      hintClassName,
      className,
      data,
      charCount,
      isEditable,
      isNotEditable,
      // those are suppressed since they also not belong to inputProps, so should be skipped
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onClick,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      refCallback,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      minMultilineHeight,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      onResize,
      ...inputProps
    } = this.props;

    const inputClasses = classnames({
      'input': true,
      'input--multiline': multiline,
      'input--focus-on-mount': focusOnMount,
      'input--is-editable': isEditable,
      'input--is-not-editable': isNotEditable,
      [`input--width-${String(width)}`]: width,
      [`input--align-${String(align)}`]: align,
      [String(className)]: className
    });

    const hintClasses = classnames({
      'input-wrapper__hint': true,
      'input-wrapper__hint--long': hint && hint.length > 1,
      [`input-wrapper__hint--align-${String(hintAlign)}`]: hint && hintAlign,
      [String(hintClassName)]: hintClassName
    });

    return (
      <section className="input-wrapper">
        {
          hint &&
            <div className={hintClasses}>{hint}</div>
        }
        {
          multiline ?
            <textarea
              className={inputClasses}
              ref={this.handleRefCallback}
              {...inputProps}
              onChange={this.handleTextareaChanged}
              onClick={this.handleClick}
              onKeyPress={this.handleKeyPress}
              onBlur={this.handleBlur}
              {...expandDataProps(data)}
            /> :
            <input
              className={inputClasses}
              ref={this.handleRefCallback}
              {...inputProps}
              onKeyPress={this.handleKeyPress}
              onBlur={this.handleBlur}
              onClick={this.handleClick}
              {...expandDataProps(data)}
            />
        }
        {
          isObject(charCount) && !isEmpty(charCount) &&
            this.renderCharacterCount()
        }
      </section>
    );
  }
}

export default Input;
